// ** React Imports
import { useEffect, useRef } from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getPosts } from "store/modules/main-pages/posts/get-posts.action";
import { closeFilters } from "store/modules/shared-filters.slice";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

// ** Custom Components
import ScrollToTop from "components/global/ScrollToTop";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import Heading from "components/typography/Heading";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import EmptyBlock from "components/layout/Block/BlockEmpty";

// ** Posts Components
import PostsList from "./components/PostsList";
import { PostsFiltersSection } from "./components/PostsFilterSection/PostsFilterSection";

// ** Custom Hooks
import usePostsMetaTags from "./hooks/usePostsMetaTags";
import usePostsInfinityScroll from "hooks/usePostsInfinityScroll";

// #####################################################

const Posts = () => {
	const { t } = useTranslation(["pages"]);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const isFiltersOpen = useSelector((state) => state.sharedFilters.isOpen);

	const dispatch = useDispatch();

	const posts = useSelector((state) => state.mainPagePosts);

	const containerRef = useRef(null);

	const { triggerRef } = usePostsInfinityScroll({ containerRef });

	const { title, metaDescription, ogMetaTags, canonicalLink } =
		usePostsMetaTags({
			minYear: posts?.minDates?.rok_dodania || 2021,
		});

	useEffect(() => {
		if (isFiltersOpen) {
			dispatch(closeFilters());
		}
		if (!(posts.loaded || posts.loading)) {
			dispatch(getPosts({ isAuth }));
		}
	}, [posts.loaded, posts.loading, isAuth]);

	// #####################################################

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title}>
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			<ScrollToTop />

			<CardsBlockWrapper>
				{/* Sekcja Filtrów */}
				<CardWrapper>
					<CardTitle>
						<Heading type="h2" data-testid="posts-heading">
							{t("pages:posts")}
						</Heading>
					</CardTitle>
					<CardBody>
						<PostsFiltersSection />
					</CardBody>
				</CardWrapper>

				{/* Główna zawartość strony */}
				<PostsList triggerRef={triggerRef} />
				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

// #####################################################

export default Posts;

// #####################################################
